<template>
  <div class="asset-select" :class="{'is-invalid': validationError}">
    <div class="asset-select__header">
      <div class="asset-select__header-icon">
        <Icon icon="fa-pro-solid:images" />
      </div>
      <div>
        <strong>Medien für dieses Objekt</strong>
        <p class="mb-0">Bilder auswählen, neu hochladen und per Drag & Drop sortieren.</p>
      </div>
      <span class="asset-select__count-pill" :title="imageCountTitle">{{ assets.length }}/{{ displayedImageLimit }}</span>
    </div>

    <Sortable
      v-if="assets.length"
      :key="sortableRenderKey"
      :list="selectedAssets"
      item-key="id"
      @end="onDragEnd"
      :options="{ handle: '.drag-handler', animation: 150 }"
      class="row"
    >
      <template #item="{ element }">
        <MediaCard
          class="col-12 col-md-6 col-xl-3 my-2"
          :draggable="true"
          :removable="true"
          :asset="element"
          :editable="true"
          :show-premium-banderole="isAssetLocked(element)"
          @edit="openAssetEdit"
          @unlink="unlinkAsset"
        />
      </template>
    </Sortable>

    <div v-else class="asset-select__empty">
      <Icon icon="fa-pro-solid:images" class="asset-select__empty-icon" />
      <div>
        <strong>Noch keine Medien zugewiesen</strong>
        <p class="mb-0">Wählen Sie vorhandene Dateien aus oder laden Sie neue direkt hoch.</p>
      </div>
    </div>

    <div class="asset-select__actions mt-3">
      <button class="btn btn-primary asset-select__manage-button" type="button" @click.prevent="openMediaLibrary">
        <Icon icon="fa-pro-solid:images" class="me-2" />
        {{ assets.length ? 'Medien verwalten' : 'Medien auswählen oder hochladen' }}
      </button>
      <span class="asset-select__count">Die Reihenfolge wird für die Ausgabe übernommen.</span>
    </div>

    <div class="mt-2">
      <div v-if="isFreePackageForBaseType" class="alert alert-warning py-2 px-3 mb-0">
        {{ freePackageHintText }}
      </div>
      <p v-else class="asset-select__hint mb-0">{{ limitHintText }}</p>
    </div>
  </div>

  <Dialog
    v-model:visible="mediaLibOpen"
    modal
    class="asset-picker-dialog"
    :style="{ width: '100vw', maxWidth: 'none', margin: 0, borderRadius: 0 }"
    :content-style="{ padding: 0, flex: '1 1 auto' }"
    header="Medien auswählen"
    @hide="onDialogHide"
  >
    <div class="asset-picker">
      <div class="asset-picker__topbar">
        <div>
          <strong>{{ pickerSelectionSummary }}</strong>
          <span class="d-block small text-muted">Klicken Sie Medien in der Übersicht an, um sie auszuwählen.</span>
        </div>
        <div v-if="selectedAssets.length" class="asset-picker__selection-preview" aria-label="Ausgewählte Medien">
          <img
            v-for="asset in selectedAssets.slice(0, 5)"
            :key="asset.id"
            :src="asset.thumbnail_url"
            :alt="asset.alt_text || ''"
          />
          <span v-if="selectedAssets.length > 5" class="asset-picker__more">+{{ selectedAssets.length - 5 }}</span>
        </div>
      </div>

      <section class="asset-picker__upload">
        <div class="asset-picker__section-title">
          <Icon icon="fa-pro-solid:cloud-arrow-up" />
          <div>
            <strong>Neue Dateien hochladen</strong>
            <span>Hochgeladene Dateien werden automatisch ausgewählt.</span>
          </div>
        </div>
        <DropZone
          v-if="remainingSlots > 0"
          ref="dropzone"
          compact
          :accept-files="uploadAcceptFiles"
          :uploading="uploading"
          @upload="onUpload"
          @abort="resetUpload"
        />
        <div v-else class="alert alert-info mb-0">
          Alle {{ maxImages }} Speicherplätze sind belegt. Entfernen Sie zuerst ein Medium aus der Auswahl, um eine neue Datei hochzuladen.
        </div>
      </section>

      <section class="asset-picker__library">
        <div class="asset-picker__section-title px-3 pt-3">
          <Icon icon="fa-pro-solid:photo-film" />
          <div>
            <strong>Vorhandene Medien</strong>
            <span>Suchen, filtern und mit einem Klick auswählen.</span>
          </div>
        </div>
        <MediaList
          :lockTypeSelect="lockTypeSelect"
          :selectedAssets="selectedAssets"
          :editable="true"
          @mediaSelected="mediaSelected"
          @mediaEdit="openAssetEdit"
        />
      </section>
    </div>

    <template #footer>
      <div class="asset-picker__footer">
        <span class="text-muted small">{{ remainingSlotsText }}</span>
        <div>
          <button type="button" class="btn btn-link me-2" @click="cancelSelection">Abbrechen</button>
          <button type="button" class="btn btn-primary" @click="assignAssets">
            <Icon icon="fa-pro-solid:check" class="me-2" />
            {{ selectedAssets.length }} Medien übernehmen
          </button>
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="assetEditOpen"
    modal
    header="Medienangaben bearbeiten"
    :style="{ width: '90vw', maxWidth: '560px' }"
    @hide="resetAssetEdit"
  >
    <div v-if="editingAsset" class="asset-edit">
      <div class="asset-edit__preview">
        <img
          v-if="editingAsset.content_type !== 'application/pdf'"
          :src="editingAsset.thumbnail_url || editingAsset.auto_url"
          :alt="assetEditForm.alt_text || ''"
        >
        <div v-else class="asset-edit__pdf">
          <Icon icon="fa-pro-solid:file-pdf" />
          PDF
        </div>
      </div>

      <div class="form-floating mb-3">
        <input
          id="asset-edit-alt-text"
          v-model.trim="assetEditForm.alt_text"
          type="text"
          class="form-control"
          :class="{'is-invalid': assetEditSubmitted && !assetEditForm.alt_text}"
          placeholder="Bildbeschreibung"
        >
        <label for="asset-edit-alt-text">Bildbeschreibung (Alt-Text)</label>
        <div v-if="assetEditSubmitted && !assetEditForm.alt_text" class="invalid-feedback">
          Bitte eine Bildbeschreibung angeben.
        </div>
      </div>

      <div class="form-floating">
        <input
          id="asset-edit-copyright"
          v-model.trim="assetEditForm.copyright"
          type="text"
          class="form-control"
          placeholder="Copyright"
        >
        <label for="asset-edit-copyright">Copyright</label>
      </div>
    </div>

    <template #footer>
      <button type="button" class="btn btn-link me-2" :disabled="assetEditSaving" @click="closeAssetEdit">Abbrechen</button>
      <button type="button" class="btn btn-primary" :disabled="assetEditSaving" @click="saveAssetEdit">
        <span v-if="assetEditSaving" class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
        {{ assetEditSaving ? 'Speichert ...' : 'Speichern' }}
      </button>
    </template>
  </Dialog>
</template>

<script>
import DropZone from "@/components/general/DropZone.vue";
import {Helpers} from "@/helpers";
import MediaCard from "@/components/media/MediaCard.vue";
import MediaList from "@/components/media/MediaList.vue";
import {useMediaStore} from "@/stores/media";
import {useUserStore} from "@/stores/user";
import Dialog from "primevue/dialog";
import {Sortable} from "sortablejs-vue3";
import {
  FREE_PACKAGE_IMAGE_HINT,
  FREE_PACKAGE_PUBLISHED_IMAGE_LIMIT,
  MAX_OBJECT_IMAGES,
  hasFreePackageForBaseType,
} from '@/utils/objectFormPolicy';

export default {
  components: {DropZone, Sortable, MediaList, MediaCard, Dialog},
  emits: ["assetSelect"],
  props: {
    assets: {type: Array, default: () => []},
    lockTypeSelect: {type: Boolean, default: false},
    validationError: {type: Boolean, default: false},
    baseType: {type: String, default: null},
  },
  setup() {
    return {
      mediaStore: useMediaStore(),
      userStore: useUserStore(),
    }
  },
  data() {
    return {
      mediaLibOpen: false,
      selectedAssets: [...this.assets],
      uploading: false,
      selectionApplied: false,
      sortableRenderKey: 0,
      assetEditOpen: false,
      assetEditSaving: false,
      assetEditSubmitted: false,
      editingAsset: null,
      assetEditForm: {
        alt_text: '',
        copyright: '',
      },
    }
  },
  computed: {
    resolvedBaseType() {
      const explicitBaseType = String(this.baseType || '').toLowerCase()
      if (explicitBaseType) return explicitBaseType

      const routeBaseType = String(this.$route?.meta?.base_type || '').toLowerCase()
      if (routeBaseType) return routeBaseType

      const routeName = String(this.$route?.name || '').toLowerCase()
      if (routeName.startsWith('accommodations.')) return 'unterkunft'
      if (routeName.startsWith('discover.')) return 'entdecken'
      if (routeName.startsWith('offers.')) return 'angebote'
      if (routeName.startsWith('events.')) return 'termine'
      return ''
    },
    isFreePackageForBaseType() {
      return hasFreePackageForBaseType(
        this.userStore.user?.organisation?.active_packages,
        this.resolvedBaseType,
      )
    },
    maxImages() { return MAX_OBJECT_IMAGES },
    freePackagePublishedImageLimit() { return FREE_PACKAGE_PUBLISHED_IMAGE_LIMIT },
    freePackageHintText() { return FREE_PACKAGE_IMAGE_HINT },
    displayedImageLimit() {
      return this.isFreePackageForBaseType
        ? this.freePackagePublishedImageLimit
        : this.maxImages
    },
    publishedSelectedCount() {
      return Math.min(this.selectedAssets.length, this.displayedImageLimit)
    },
    imageCountTitle() {
      if (!this.isFreePackageForBaseType) return `Maximal ${this.maxImages} Bilder auswählbar.`
      return `Im free-Paket werden ${this.displayedImageLimit} Bilder ausgegeben; weitere Bilder bleiben gespeichert.`
    },
    pickerSelectionSummary() {
      if (!this.isFreePackageForBaseType) {
        return `${this.selectedAssets.length} von ${this.maxImages} ausgewählt`
      }

      return `${this.selectedAssets.length} ausgewählt, ${this.publishedSelectedCount} von ${this.displayedImageLimit} für die Ausgabe`
    },
    remainingSlotsText() {
      if (!this.isFreePackageForBaseType) return `${this.remainingSlots} Plätze frei`

      return `${this.remainingSlots} von ${this.maxImages} Speicherplätzen frei`
    },
    remainingSlots() { return Math.max(this.maxImages - this.selectedAssets.length, 0) },
    uploadAcceptFiles() {
      return this.lockTypeSelect
        ? ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']
        : ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.pdf']
    },
    limitHintText() {
      return `Sie können maximal ${this.maxImages} Bilder verwenden.`
    },
  },
  watch: {
    assets: {
      deep: true,
      handler(newAssets) {
        if (!this.mediaLibOpen) this.selectedAssets = [...newAssets]
      },
    },
  },
  methods: {
    openMediaLibrary() {
      this.selectedAssets = [...this.assets]
      this.selectionApplied = false
      this.mediaLibOpen = true
    },
    mediaSelected(asset) {
      const index = this.selectedAssets.findIndex((item) => item.id === asset.id)
      if (index !== -1) {
        this.selectedAssets.splice(index, 1)
        return
      }
      if (this.remainingSlots <= 0) {
        Helpers.emitter.emit('error', `Maximal ${this.maxImages} Bilder erlaubt.`)
        return
      }
      this.selectedAssets.push(asset)
    },
    assignAssets() {
      this.selectionApplied = true
      this.$emit('assetSelect', [...this.selectedAssets])
      this.mediaLibOpen = false
    },
    cancelSelection() {
      this.mediaLibOpen = false
    },
    onDialogHide() {
      if (!this.selectionApplied) this.selectedAssets = [...this.assets]
      this.selectionApplied = false
      this.resetUpload()
    },
    resetUpload() {
      this.$refs.dropzone?.reset?.()
    },
    openAssetEdit(asset) {
      this.editingAsset = asset
      this.assetEditForm = {
        alt_text: asset?.alt_text || '',
        copyright: asset?.copyright || '',
      }
      this.assetEditSubmitted = false
      this.assetEditOpen = true
    },
    closeAssetEdit() {
      this.assetEditOpen = false
    },
    resetAssetEdit() {
      if (this.assetEditSaving) return
      this.editingAsset = null
      this.assetEditForm = {
        alt_text: '',
        copyright: '',
      }
      this.assetEditSubmitted = false
    },
    replaceAsset(updatedAsset) {
      this.selectedAssets = this.selectedAssets.map((asset) => (
        asset.id === updatedAsset.id ? {...asset, ...updatedAsset} : asset
      ))
      this.mediaStore.assets = this.mediaStore.assets.map((asset) => (
        asset.id === updatedAsset.id ? {...asset, ...updatedAsset} : asset
      ))
    },
    async saveAssetEdit() {
      this.assetEditSubmitted = true
      if (!this.editingAsset || !this.assetEditForm.alt_text) return

      this.assetEditSaving = true
      try {
        const {data} = await this.mediaStore.update(
          this.editingAsset.id,
          this.userStore.user.organisation.kdnr,
          {
            asset: {
              alt_text: this.assetEditForm.alt_text,
              copyright: this.assetEditForm.copyright,
              lock_version: this.editingAsset.lock_version,
            },
          }
        )
        const updatedAsset = {
          ...this.editingAsset,
          ...(data?.asset || data || {}),
          alt_text: this.assetEditForm.alt_text,
          copyright: this.assetEditForm.copyright,
        }
        const wasSelected = this.selectedAssets.some((asset) => asset.id === updatedAsset.id)
        this.replaceAsset(updatedAsset)
        if (wasSelected) this.$emit('assetSelect', [...this.selectedAssets])
        Helpers.emitter.emit('success', 'Medienangaben gespeichert.')
        this.assetEditSaving = false
        this.assetEditOpen = false
      } catch (error) {
        if (error.response?.status === 409) {
          Helpers.emitter.emit('error', 'Das Medium wurde zwischenzeitlich geändert. Bitte öffnen Sie es erneut.')
        } else {
          Helpers.emitter.emit('error', error.response?.data?.message || error.message || 'Medienangaben konnten nicht gespeichert werden')
        }
      } finally {
        this.assetEditSaving = false
      }
    },
    async onUpload(assetData) {
      const acceptedAssets = assetData.slice(0, this.remainingSlots)
      const skippedCount = assetData.length - acceptedAssets.length

      if (!acceptedAssets.length) {
        Helpers.emitter.emit('error', `Maximal ${this.maxImages} Bilder erlaubt.`)
        return
      }

      this.uploading = true
      try {
        const uploads = await Promise.all(
          acceptedAssets.map((asset) => this.mediaStore.createAsset(this.userStore.user.organisation.kdnr, asset))
        )
        uploads.forEach(({data}) => {
          this.mediaStore.assets = [data, ...this.mediaStore.assets.filter((asset) => asset.id !== data.id)]
          if (!this.selectedAssets.some((asset) => asset.id === data.id)) this.selectedAssets.push(data)
        })
        this.mediaStore.total += uploads.length
        this.resetUpload()
        Helpers.emitter.emit('success', `${uploads.length} Datei(en) hochgeladen und ausgewählt.`)
        if (skippedCount) {
          Helpers.emitter.emit('error', `${skippedCount} Datei(en) wurden wegen des Limits nicht hochgeladen.`)
        }
      } catch (error) {
        Helpers.emitter.emit('error', error.response?.data?.message || error.message || 'Upload fehlgeschlagen')
      } finally {
        this.uploading = false
      }
    },
    unlinkAsset(assetId) {
      this.selectedAssets = this.assets.filter((asset) => asset.id !== assetId)
      this.$emit('assetSelect', [...this.selectedAssets])
    },
    isAssetLocked(asset) {
      const assetIndex = this.selectedAssets.findIndex((item) => item.id === asset.id)
      return this.isFreePackageForBaseType && assetIndex >= this.freePackagePublishedImageLimit
    },
    onDragEnd(event) {
      const oldIndex = event?.oldIndex
      const newIndex = event?.newIndex

      if (
        oldIndex == null ||
        newIndex == null ||
        oldIndex === newIndex ||
        oldIndex < 0 ||
        newIndex < 0 ||
        oldIndex >= this.selectedAssets.length ||
        newIndex >= this.selectedAssets.length
      ) {
        this.$emit('assetSelect', [...this.selectedAssets])
        return
      }

      const reorderedAssets = [...this.selectedAssets]
      const [movedAsset] = reorderedAssets.splice(oldIndex, 1)
      reorderedAssets.splice(newIndex, 0, movedAsset)
      this.selectedAssets = reorderedAssets
      this.sortableRenderKey += 1
      this.$emit('assetSelect', [...this.selectedAssets])
    },
  },
}
</script>

<style scoped lang="scss">
@import "src/scss/constants";

.asset-select.is-invalid {
  padding: 0.5rem;
  border: 1px solid var(--bs-danger);
  border-radius: 6px;
}

.asset-select {
  padding: 1rem;
  border: 1px solid #d6e1e8;
  background: #fff;
  box-shadow: 0 8px 22px rgba(16, 50, 74, 0.05);
}

.asset-select__header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.85rem;
  margin: -1rem -1rem 1rem;
  padding: 1rem;
  border-bottom: 1px solid #dce5eb;
  background: linear-gradient(135deg, #f6fafc 0%, #edf5f9 100%);
  color: #27445d;
}

.asset-select__header p {
  margin-top: 0.15rem;
  color: #687d8e;
  font-size: 0.85rem;
}

.asset-select__header-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  color: white;
  background: $manet_blue;
  font-size: 1.1rem;
}

.asset-select__count-pill {
  min-width: 58px;
  padding: 0.35rem 0.55rem;
  border: 1px solid #bfd2df;
  background: white;
  color: $manet_blue_dark;
  font-size: 0.82rem;
  font-weight: 700;
  text-align: center;
}

.asset-select__empty {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  color: #40586d;
  background: #f7fafc;
  border: 1px dashed #9eb3c5;
}

.asset-select__empty-icon {
  flex: 0 0 auto;
  font-size: 2rem;
  color: $manet_blue;
}

.asset-select__actions,
.asset-picker__footer,
.asset-picker__topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.asset-select__count,
.asset-select__hint {
  color: #5a6f82;
  font-size: 0.88rem;
}

.asset-select__manage-button {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.8rem;
  font-size: 0.9rem;
  font-weight: 600;
}

.asset-picker {
  background: #f4f7f9;
}

.asset-picker__topbar {
  position: sticky;
  top: 0;
  z-index: 4;
  padding: 0.75rem 1rem;
  background: white;
  border-bottom: 1px solid #dce4ea;
}

.asset-picker__selection-preview {
  display: flex;
  align-items: center;
}

.asset-picker__selection-preview img,
.asset-picker__more {
  width: 38px;
  height: 38px;
  margin-left: -7px;
  border: 2px solid white;
  border-radius: 50%;
  object-fit: cover;
  background: #dce6ed;
}

.asset-picker__more {
  display: grid;
  place-items: center;
  color: #27445d;
  font-size: 0.75rem;
  font-weight: 700;
}

.asset-picker__upload,
.asset-picker__library {
  margin: 1rem;
  background: white;
  border: 1px solid #dce4ea;
}

.asset-picker__upload {
  padding: 1rem;
}

.asset-picker__section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: #27445d;
}

.asset-picker__section-title > svg {
  font-size: 1.25rem;
  color: $manet_blue;
}

.asset-picker__section-title span {
  display: block;
  color: #6a7d8d;
  font-size: 0.8rem;
}

.asset-picker__footer {
  width: 100%;
}

.asset-edit {
  display: grid;
  gap: 1rem;
}

.asset-edit__preview {
  overflow: hidden;
  border: 1px solid #dce4ea;
  background: #eef4f7;
  aspect-ratio: 16 / 9;
}

.asset-edit__preview img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.asset-edit__pdf {
  display: grid;
  height: 100%;
  place-items: center;
  color: #8d3030;
  font-weight: 700;
}

.asset-edit__pdf > svg {
  font-size: 2.2rem;
}

@media (max-width: 575.98px) {
  .asset-select__header {
    grid-template-columns: auto 1fr;
  }

  .asset-select__count-pill {
    grid-column: 1 / -1;
  }

  .asset-select__actions,
  .asset-picker__footer {
    align-items: stretch;
    flex-direction: column;
  }

  .asset-picker__footer > div,
  .asset-picker__footer .btn-primary {
    width: 100%;
  }
}
</style>

<style lang="scss">
.asset-picker-dialog.p-dialog {
  width: 100vw !important;
  max-width: none !important;
  max-height: 100vh !important;
  margin: 0 !important;
  border-radius: 0 !important;
}

.asset-picker-dialog .p-dialog-header,
.asset-picker-dialog .p-dialog-content,
.asset-picker-dialog .p-dialog-footer {
  border-radius: 0 !important;
}
</style>
